import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import styles from "./Docs.module.css";

/**
 * Product documentation. Describes only what the product does today; items
 * not yet built are explicitly labelled "Roadmap". Facts are sourced from
 * docs/ARCHITECTURE.md and docs/FEATURE_CATALOG.md — nothing here is invented.
 */

interface NavItem {
  id: string;
  label: string;
}

const NAV: NavItem[] = [
  { id: "overview", label: "Overview" },
  { id: "brand-brain", label: "The brand brain" },
  { id: "brand-kinds", label: "Brand kinds" },
  { id: "generation", label: "Draft generation" },
  { id: "approval-queue", label: "Approval queue" },
  { id: "scheduling", label: "Scheduling & publishing" },
  { id: "product-sources", label: "Product sources" },
  { id: "roadmap", label: "On the roadmap" },
];

const BRAND_KINDS: Array<{ kind: string; blurb: string }> = [
  {
    kind: "ecommerce",
    blurb:
      "Sells products. Pulls from a Shopify or WooCommerce catalog (or a manual one), so generated posts can reference real titles, prices, and product photos.",
  },
  {
    kind: "service",
    blurb:
      "Sells services rather than a catalog. The brand brain skips product sections entirely; generation leans on voice, audience, and offer descriptions.",
  },
  {
    kind: "creator",
    blurb:
      "An individual or personality. No product catalog by default — content is built around the creator's voice, topics, and audience.",
  },
  {
    kind: "media",
    blurb:
      "A publication or content brand. Like creator, but oriented around editorial topics and ongoing series rather than a single voice.",
  },
];

const VALIDATORS: Array<{ name: string; detail: string; status: "live" | "roadmap" }> = [
  { name: "Banned terms", detail: "Regex list from the brand's things-to-avoid.", status: "live" },
  { name: "Off-voice terms", detail: "Words the brand's tone lexicon says to avoid.", status: "live" },
  { name: "Hashtag deny list + count", detail: "Per-platform hashtag rules and maximum count.", status: "live" },
  { name: "Caption length", detail: "Per-platform maximum caption length.", status: "live" },
  { name: "Claim check", detail: "A Claude pass that flags unsupported claims.", status: "roadmap" },
  { name: "Duplicate caption", detail: "Fuzzy match against recently published posts.", status: "roadmap" },
  { name: "AI-slop visual check", detail: "A Claude vision pass when an image is attached.", status: "roadmap" },
];

function Badge({ kind }: { kind: "live" | "roadmap" }) {
  return (
    <span className={kind === "live" ? styles.badgeLive : styles.badgeRoadmap}>
      {kind === "live" ? "Live" : "Roadmap"}
    </span>
  );
}

export function Docs() {
  return (
    <>
      <MarketingNav />

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.kicker}>Documentation</p>
            <h1 className={styles.title}>How Notamen works</h1>
            <p className={styles.lead}>
              Teach it your brand once, then generate, review, and schedule
              on-brand posts. This page describes what the product does today —
              anything still being built is marked as roadmap.
            </p>
          </div>
        </section>

        <div className={styles.body}>
          <nav className={styles.sidebar} aria-label="Documentation sections">
            <ul className={styles.navList}>
              {NAV.map((item) => (
                <li key={item.id}>
                  <a className={styles.navLink} href={`#${item.id}`}>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className={styles.content}>
            <section id="overview" className={styles.section}>
              <h2 className={styles.h2}>Overview</h2>
              <p className={styles.p}>
                Notamen is multi-tenant: one account can host many{" "}
                <strong>workspaces</strong>, and each workspace holds many{" "}
                <strong>brands</strong>. Everything below is scoped to a single
                brand inside a workspace.
              </p>
              <p className={styles.p}>
                The flow is the same for every brand: build a brand brain →
                generate draft variants with AI → review them in the approval
                queue → schedule and publish. Each step is described below.
              </p>
            </section>

            <section id="brand-brain" className={styles.section}>
              <h2 className={styles.h2}>The brand brain</h2>
              <p className={styles.p}>
                The brand brain is the structured profile that teaches the AI
                who your brand is. It captures the basics, your audience, voice
                and tone (including a lexicon of words to use and avoid),
                products, things to avoid, visual style, per-platform rules
                (caption limits, hashtag rules, deny lists), and recommended
                hashtags.
              </p>
              <p className={styles.p}>
                You edit the brand brain through a sectioned form in the app.
                When a draft is generated, the brain is rendered into a single
                cacheable instruction block for Claude — and only the sections
                that apply to your brand's kind are included, so a creator brand
                with no products never gets product instructions.
              </p>
              <div className={styles.callout}>
                The brand brain is also what the safety rails check against:
                banned terms, off-voice words, and platform rules all come from
                here.
              </div>
            </section>

            <section id="brand-kinds" className={styles.section}>
              <h2 className={styles.h2}>The four brand kinds</h2>
              <p className={styles.p}>
                Every brand has a <code>kind</code>. The kind decides which
                parts of the brand brain are collected and which instructions
                the AI receives. Shared code never assumes a brand sells
                products.
              </p>
              <div className={styles.cardGrid}>
                {BRAND_KINDS.map((bk) => (
                  <div className={styles.card} key={bk.kind}>
                    <h3 className={styles.cardTitle}>{bk.kind}</h3>
                    <p className={styles.cardBody}>{bk.blurb}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="generation" className={styles.section}>
              <h2 className={styles.h2}>Draft generation</h2>
              <p className={styles.p}>
                You give the generator an input (an idea, a product, a prompt)
                plus the platforms and content kinds you want. It runs Claude —
                either via the Anthropic API or Amazon Bedrock — against your
                brand brain and produces multiple variants per request.
              </p>
              <p className={styles.p}>
                You can ask for more than one provider in a single request; the
                app tags each resulting draft with the engine that produced it
                so you can compare them side by side. Generation runs as an
                async job: the request returns a job id immediately, and the app
                polls until the drafts are ready.
              </p>
              <p className={styles.p}>
                Every generated draft is run through the safety-rail validators
                before you ever see it. Today four checks run; three more are on
                the roadmap.
              </p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Check</th>
                      <th>What it does</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {VALIDATORS.map((v) => (
                      <tr key={v.name}>
                        <td>{v.name}</td>
                        <td>{v.detail}</td>
                        <td>
                          <Badge kind={v.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="approval-queue" className={styles.section}>
              <h2 className={styles.h2}>The approval queue</h2>
              <p className={styles.p}>
                Generated drafts land in the approval queue. You can filter by
                status (pending, approved, scheduled, published, failed,
                rejected) and by platform, sort newest or oldest, and bulk-select
                to approve many at once.
              </p>
              <p className={styles.p}>
                Selecting a draft shows its variants with a real media preview,
                a detail view, and a validator view listing every warning. Each
                draft carries a comment thread, so reviewers can discuss it in
                place — every comment records the author's email and role.
              </p>
              <p className={styles.p}>The actions on a draft are:</p>
              <ul className={styles.list}>
                <li>
                  <strong>Approve</strong> — marks the draft approved. If a
                  validator returned a blocking warning, approval is refused
                  unless you explicitly override it and give a reason, which is
                  stored on the draft.
                </li>
                <li>
                  <strong>Reject</strong> — marks the draft rejected with an
                  optional reason.
                </li>
                <li>
                  <strong>Edit</strong> — edits every variant's text in place
                  (body, hook, hashtags, CTA, image brief, notes). On save the
                  draft is re-validated against the current brand brain and
                  returns to pending.
                </li>
                <li>
                  <strong>Schedule</strong> — sets a future publish time.
                  Scheduling follows a linear flow: a draft must already be
                  approved before it can be scheduled.
                </li>
              </ul>
              <div className={styles.callout}>
                The block-level safety rail is enforced on the server, not just
                in the UI. A draft with a blocking warning cannot reach{" "}
                <code>approved</code> without a stored override and reason —
                even if a request tries to skip the UI.
              </div>
            </section>

            <section id="scheduling" className={styles.section}>
              <h2 className={styles.h2}>Scheduling &amp; publishing</h2>
              <p className={styles.p}>
                Once a draft is approved you can schedule it for a future time.
                The target platforms are Instagram, Facebook, and TikTok, and
                per-platform rules (caption length, hashtag limits, deny lists)
                are part of the brand brain and enforced by the validators.
              </p>
              <p className={styles.p}>
                The publish pipeline that actually posts to each platform — Meta
                Graph for Instagram and Facebook, the TikTok Content Posting API
                for TikTok — together with connecting your social accounts and
                firing scheduled posts is on the roadmap. The scheduling flow,
                approval gates, and per-platform rules described above are live
                today.
              </p>
            </section>

            <section id="product-sources" className={styles.section}>
              <h2 className={styles.h2}>Product sources</h2>
              <p className={styles.p}>
                Brands that sell products declare a product source. The
                generator and sync layer go through a single pluggable adapter,
                so the rest of the app behaves identically regardless of where
                products come from.
              </p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>What it is</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <code>shopify</code>
                      </td>
                      <td>Read-only Shopify Storefront API (GraphQL).</td>
                    </tr>
                    <tr>
                      <td>
                        <code>woocommerce</code>
                      </td>
                      <td>WooCommerce REST API v3 (consumer key + secret).</td>
                    </tr>
                    <tr>
                      <td>
                        <code>manual</code>
                      </td>
                      <td>A catalog you type directly into the app.</td>
                    </tr>
                    <tr>
                      <td>
                        <code>none</code>
                      </td>
                      <td>
                        No catalog. Used by service, creator, and media brands.
                        Generation handles the empty product case.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className={styles.note}>
                The Shopify, WooCommerce, manual, and none adapters are live.
                The hourly worker that automatically re-syncs a connected
                catalog is on the roadmap.
              </p>
            </section>

            <section id="roadmap" className={styles.section}>
              <h2 className={styles.h2}>On the roadmap</h2>
              <p className={styles.p}>
                These are committed but not yet shipped. They are listed here so
                this page stays honest about what does and doesn't work today.
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>Slack approvals</strong> — approve, reject, edit, and
                  schedule drafts from interactive Slack messages, plus a daily
                  digest of pending drafts.
                </li>
                <li>
                  <strong>Publishing pipeline</strong> — actually posting
                  approved, scheduled drafts to Instagram, Facebook, and TikTok,
                  with status written back to the draft.
                </li>
                <li>
                  <strong>Connected social accounts</strong> — OAuth to connect
                  each platform and store tokens.
                </li>
                <li>
                  <strong>Calendar view</strong> — month, week, and list views
                  of approved, scheduled, and published posts.
                </li>
                <li>
                  <strong>Media library</strong> — upload, tag, and browse
                  photos, including per-product galleries.
                </li>
                <li>
                  <strong>Metrics &amp; feedback loop</strong> — pulling
                  post-performance metrics and feeding top performers back into
                  generation.
                </li>
                <li>
                  <strong>Remaining safety rails</strong> — the claim check,
                  duplicate-caption check, and AI-slop visual check noted above.
                </li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </>
  );
}

export default Docs;
